/**
 * Invitation Service
 * 
 * Handles the creation, validation, and application of workspace invitations.
 * This enables invitation-only signup with pre-assigned workspace and roles.
 */

import { getSafeSupabaseClient, isSupabaseAvailable } from './supabaseSync';
import { getTypedSupabaseClient } from './typedSupabaseClient';
import type { ActorRole } from '../types/prioritization/roles';

// ============================================================================
// Types
// ============================================================================

/**
 * Invitation record as stored in the database
 * 
 * Permissions model:
 * - roles: ActorRole[] - WHAT they can do (the actor roles)
 * - orgUnitId: string - WHERE they can do it (place in org chart)
 */
export interface Invitation {
  id: string;
  token: string;
  /** Invitee's name (for display and user creation) */
  name: string | null;
  email: string | null;
  workspaceId: string | null;
  tenantId: string | null;
  /** Role names to assign (must exist in roles table) - defines WHAT the user can do */
  roles: string[];
  /** Org unit where roles apply - defines WHERE the user can act */
  orgUnitId: string | null;
  invitedBy: string | null;
  expiresAt: Date;
  usedAt: Date | null;
  usedBy: string | null;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Parameters for creating a new invitation
 * 
 * Permissions model:
 * - roles: WHAT - the actor roles the user will have
 * - orgUnitId: WHERE - the place in the org chart where roles apply
 */
export interface CreateInvitationParams {
  /** Invitee's name */
  name?: string;
  /** Optional: restrict to specific email */
  email?: string;
  /** Target workspace ID */
  workspaceId: string;
  /** Target tenant ID */
  tenantId: string;
  /** Actor roles to assign upon acceptance (WHAT they can do) */
  roles?: ActorRole[];
  /** Org unit where roles apply (WHERE they can act) */
  orgUnitId?: string;
  /** User ID of the person creating the invite */
  invitedBy: string;
  /** Custom expiration (defaults to 7 days) */
  expiresInDays?: number;
}

/**
 * Result of validating an invitation
 */
export interface InvitationValidationResult {
  valid: boolean;
  invitation?: Invitation;
  error?: 'not_found' | 'expired' | 'already_used' | 'email_mismatch';
  message?: string;
}

/**
 * Invitation with related workspace/tenant info for display
 */
export interface InvitationWithDetails extends Invitation {
  workspaceName?: string;
  tenantName?: string;
  invitedByName?: string;
  orgUnitName?: string;
}

// ============================================================================
// Storage Key for Pending Invitation
// ============================================================================

const PENDING_INVITE_KEY = 'peak-priorities-pending-invite';

/**
 * Store a pending invitation token to be applied after signup
 */
export function storePendingInvite(token: string): void {
  try {
    sessionStorage.setItem(PENDING_INVITE_KEY, token);
  } catch (e) {
    console.warn('[InvitationService] Failed to store pending invite:', e);
  }
}

/**
 * Get the pending invitation token (if any)
 */
export function getPendingInvite(): string | null {
  try {
    return sessionStorage.getItem(PENDING_INVITE_KEY);
  } catch {
    return null;
  }
}

/**
 * Clear the pending invitation token
 */
export function clearPendingInvite(): void {
  try {
    sessionStorage.removeItem(PENDING_INVITE_KEY);
  } catch (e) {
    console.warn('[InvitationService] Failed to clear pending invite:', e);
  }
}

// ============================================================================
// Database Operations
// ============================================================================

/**
 * Create a new invitation.
 *
 * Goes through `create_workspace_invitation` (migration 748, SECURITY DEFINER):
 * the server checks the actor is a workspace admin/owner (auth.uid()-bound —
 * `params.invitedBy` is not sent), validates role names and the org unit, and
 * mints the token. This replaced a plain `.from('invitations').insert(...)`
 * that RLS happened to allow but that bypassed all of that validation, and
 * that the public /v1 API could never call at all (build contract §2.3a: an
 * authority transition goes through one transactional RPC, not a direct
 * client write).
 *
 * `params.tenantId` is accepted for backward compatibility with existing
 * callers' signatures but is not sent to the RPC — the server derives the
 * tenant from the workspace itself, which is the source of truth.
 */
export async function createInvitation(params: CreateInvitationParams): Promise<{ invitation: Invitation | null; error: string | null }> {
  if (!isSupabaseAvailable()) {
    return { invitation: null, error: 'Supabase not available' };
  }

  try {
    const { data, error } = await getTypedSupabaseClient().rpc('create_workspace_invitation', {
      p_workspace_id: params.workspaceId,
      p_email: params.email || undefined,
      p_name: params.name || undefined,
      p_roles: params.roles && params.roles.length > 0 ? params.roles : undefined,
      p_org_unit_id: params.orgUnitId || undefined,
      p_expires_in_days: params.expiresInDays,
    });

    if (error) {
      console.error('[InvitationService] create_workspace_invitation failed:', error.message);
      return { invitation: null, error: error.message };
    }

    const res = (data ?? {}) as Record<string, unknown> & { success?: boolean; error?: string };
    if (res.success !== true) {
      return { invitation: null, error: typeof res.error === 'string' ? res.error : 'The invitation could not be created' };
    }

    return { invitation: transformFromRpcResult(res, params.tenantId, params.invitedBy), error: null };
  } catch (err) {
    console.error('[InvitationService] Exception creating invitation:', err);
    return { invitation: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Validate an invitation token
 */
export async function validateInvitation(token: string, email?: string): Promise<InvitationValidationResult> {
  if (!isSupabaseAvailable()) {
    return { valid: false, error: 'not_found', message: 'Database not available' };
  }

  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    return { valid: false, error: 'not_found', message: 'Database client not available' };
  }

  try {
    // Exact-token lookup through a SECURITY DEFINER RPC (migration 734): this
    // runs before sign-in, and anon no longer has any access to the table.
    const { data: rpcData, error } = await supabase.rpc('get_invitation_by_token', { p_token: token });
    const data = rpcData as Record<string, unknown> | null;

    if (error || !data) {
      return { valid: false, error: 'not_found', message: 'Invitation not found' };
    }

    const invitation = transformFromDb(data);

    // Check if already used
    if (invitation.usedAt) {
      return { valid: false, error: 'already_used', message: 'This invitation has already been used' };
    }

    // Check if expired
    if (new Date(invitation.expiresAt) < new Date()) {
      return { valid: false, error: 'expired', message: 'This invitation has expired' };
    }

    // Check email restriction if applicable
    if (invitation.email && email && invitation.email.toLowerCase() !== email.toLowerCase()) {
      return { valid: false, error: 'email_mismatch', message: 'This invitation is for a different email address' };
    }

    return { valid: true, invitation };
  } catch (err) {
    console.error('[InvitationService] Exception validating invitation:', err);
    return { valid: false, error: 'not_found', message: 'Error validating invitation' };
  }
}

/**
 * Get invitation details with workspace/tenant names for display
 */
export async function getInvitationDetails(token: string): Promise<InvitationWithDetails | null> {
  if (!isSupabaseAvailable()) return null;

  const supabase = getSafeSupabaseClient();
  if (!supabase) return null;

  try {
    // Same RPC as validateInvitation (migration 734). It returns the row plus
    // `workspaces` / `tenants` / `inviter` name objects, shaped like the
    // PostgREST embed this replaced.
    const { data: rpcData, error } = await supabase.rpc('get_invitation_by_token', { p_token: token });
    const data = rpcData as Record<string, unknown> | null;

    if (error || !data) return null;

    const invitation = transformFromDb(data);
    return {
      ...invitation,
      workspaceName: (data.workspaces as { name?: string } | null)?.name,
      tenantName: (data.tenants as { name?: string } | null)?.name,
      invitedByName: (data.inviter as { name?: string } | null)?.name,
    };
  } catch (err) {
    console.error('[InvitationService] Exception getting invitation details:', err);
    return null;
  }
}

/**
 * Apply a pending invitation to the signed-in user.
 *
 * One server call (migration 722, `apply_invitation`): membership, tenant
 * membership, role assignments at the invite's org unit or workspace, and
 * the used-stamp — under the ratification-grade rules the accept-invite edge
 * function already applies for brand-new accounts. This replaced direct
 * client inserts into three RLS-locked tables that had been failing silently
 * since migration 197 (PRD-delegable-grants FR-7 P0-b). The email restriction
 * is enforced server-side against the session's email, not `userId`.
 *
 * `userId` is kept for callers' signatures; the server uses auth.uid().
 */
export async function applyInvitation(token: string, userId: string): Promise<{ success: boolean; error: string | null }> {
  if (!isSupabaseAvailable()) {
    return { success: false, error: 'Supabase not available' };
  }
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    return { success: false, error: 'Supabase client not available' };
  }
  try {
    const { data, error } = await supabase.rpc('apply_invitation', { p_token: token });
    if (error) {
      console.error('[InvitationService] apply_invitation failed:', error.message);
      return { success: false, error: error.message };
    }
    const res = (data ?? {}) as { success?: boolean; error?: string; already_applied?: boolean; roles_missing?: string[] };
    if (!res.success) {
      return { success: false, error: res.error ?? 'Could not apply invitation' };
    }
    clearPendingInvite();
    if (res.roles_missing && res.roles_missing.length > 0) {
      console.warn(`[InvitationService] Invitation for ${userId} named roles that do not exist:`, res.roles_missing);
    }
    console.log(`[InvitationService] Applied invitation for user ${userId}${res.already_applied ? ' (already applied)' : ''}`);
    return { success: true, error: null };
  } catch (err) {
    console.error('[InvitationService] Exception applying invitation:', err);
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * List invitations for a workspace (for admin view)
 */
export async function listInvitations(workspaceId: string): Promise<InvitationWithDetails[]> {
  if (!isSupabaseAvailable()) return [];

  const supabase = getSafeSupabaseClient();
  if (!supabase) return [];

  try {
    const { data, error } = await supabase
      .from('invitations')
      .select(`
        *,
        inviter:invited_by(name, email),
        used_by_user:used_by(name, email)
      `)
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false });

    if (error || !data) return [];

    return data.map(row => ({
      ...transformFromDb(row),
      invitedByName: (row.inviter as { name?: string } | null)?.name,
    }));
  } catch (err) {
    console.error('[InvitationService] Exception listing invitations:', err);
    return [];
  }
}

/**
 * Reset an invitation that was marked as used but failed to apply
 * This clears the used_at and used_by fields so it can be retried
 */
export async function resetInvitation(invitationId: string): Promise<{ success: boolean; error: string | null }> {
  if (!isSupabaseAvailable()) {
    return { success: false, error: 'Supabase not available' };
  }

  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    return { success: false, error: 'Supabase client not available' };
  }

  try {
    const { error } = await supabase
      .from('invitations')
      .update({
        used_at: null,
        used_by: null,
      })
      .eq('id', invitationId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, error: null };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

// Revoking an invitation is `revokeWorkspaceInvitation` in
// `src/lib/reversals/reversalsService.ts` (migration 748:
// `revoke_workspace_invitation` — reason required, keeps the row, records the
// reversal), called through `ReverseActionButton`. The old
// `revokeInvitation` here did a bare `.delete()`, which erased the record of
// who was invited and who withdrew it (canon 27 §6: a reversal is recorded,
// never an erasure) and could not be reached by the public /v1 API. It has
// been removed rather than kept as a second, unused path.

// ============================================================================
// Email
// ============================================================================

/** What happened when we asked the server to email an invitation. */
export type SendInvitationEmailResult = { sent: true } | { sent: false; reason: string };

/**
 * Email an invitation to the address on it, through the `send-invite` edge
 * function (the browser cannot hold the email provider key). The server checks
 * the caller is the inviter or a workspace admin, and refuses accepted,
 * revoked/expired and address-less invitations.
 *
 * Never throws: every failure comes back as `{ sent: false, reason }` so the
 * UI can say plainly that the email did not go out and keep the link visible.
 */
export async function sendInvitationEmail(invitationId: string): Promise<SendInvitationEmailResult> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return { sent: false, reason: 'Not connected to the server.' };

  try {
    const { data, error } = await supabase.functions.invoke('send-invite', {
      body: { invitation_id: invitationId },
    });

    if (error) {
      // A non-2xx reply carries our { sent: false, reason } body on `context`.
      const ctx = (error as unknown as { context?: unknown }).context;
      if (ctx && typeof (ctx as Response).json === 'function') {
        try {
          const body = (await (ctx as Response).json()) as { reason?: unknown };
          if (typeof body?.reason === 'string' && body.reason) {
            return { sent: false, reason: body.reason };
          }
        } catch {
          // fall through to the generic message
        }
      }
      return { sent: false, reason: error.message || 'The email service could not be reached.' };
    }

    const body = (data ?? {}) as { sent?: unknown; reason?: unknown };
    if (body.sent === true) return { sent: true };
    return {
      sent: false,
      reason: typeof body.reason === 'string' && body.reason ? body.reason : 'The email was not sent.',
    };
  } catch (err) {
    return { sent: false, reason: err instanceof Error ? err.message : 'The email was not sent.' };
  }
}

/**
 * The words the admin sees after we tried to email an invitation. On failure
 * the link stays the way in: say so, and say why.
 */
export function inviteEmailFeedback(
  email: string,
  result: SendInvitationEmailResult,
): { sent: boolean; message: string } {
  if (result.sent) return { sent: true, message: `Invitation emailed to ${email}` };
  const reason = result.reason.trim().replace(/[.\s]+$/, '');
  return {
    sent: false,
    message: `Email not sent — copy the link and send it yourself${reason ? ` (${reason})` : ''}`,
  };
}

// ============================================================================
// URL Helpers
// ============================================================================

/**
 * Generate an invite URL from a token
 */
export function getInviteUrl(token: string): string {
  const baseUrl = window.location.origin;
  return `${baseUrl}/invite/${token}`;
}

// ============================================================================
// Transform Helpers
// ============================================================================

/**
 * Transform database record to TypeScript object
 */
function transformFromDb(row: Record<string, unknown>): Invitation {
  return {
    id: row.id as string,
    token: row.token as string,
    name: row.name as string | null,
    email: row.email as string | null,
    workspaceId: row.workspace_id as string | null,
    tenantId: row.tenant_id as string | null,
    roles: (row.roles as string[]) || [],
    orgUnitId: row.org_unit_id as string | null,
    invitedBy: row.invited_by as string | null,
    expiresAt: new Date(row.expires_at as string),
    usedAt: row.used_at ? new Date(row.used_at as string) : null,
    usedBy: row.used_by as string | null,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
  };
}

/**
 * Transform `create_workspace_invitation`'s success payload into an
 * `Invitation`. The RPC returns only the fields it authored (id, token,
 * workspace_id, email, name, roles, org_unit_id, invited_by, expires_at,
 * created_at) — it does not echo `tenant_id` (the server derives it from the
 * workspace, not from the caller), so it is filled in from the params the
 * caller already had. A brand-new invitation has no `used_at`/`used_by`, and
 * `updated_at` equals `created_at` until the first change (e.g. a revoke).
 */
function transformFromRpcResult(row: Record<string, unknown>, tenantId: string, fallbackInvitedBy: string): Invitation {
  const createdAt = row.created_at as string;
  return {
    id: row.id as string,
    token: row.token as string,
    name: (row.name as string | null) ?? null,
    email: (row.email as string | null) ?? null,
    workspaceId: (row.workspace_id as string | null) ?? null,
    tenantId,
    roles: (row.roles as string[]) || [],
    orgUnitId: (row.org_unit_id as string | null) ?? null,
    invitedBy: (row.invited_by as string | null) ?? fallbackInvitedBy,
    expiresAt: new Date(row.expires_at as string),
    usedAt: null,
    usedBy: null,
    createdAt: new Date(createdAt),
    updatedAt: new Date(createdAt),
  };
}
